import { useRolodexEntries } from 'features/rolodex'
import sortBy from 'lodash/sortBy'
import type {
  InspectionTemperature,
  RobotData,
  SensorSerialNumbers,
} from 'pages/reporting/reports/rug/rugReportData'
import { ReportedAssetType } from 'pages/reporting/reports/rug/types'
import { normalizeInspectionTemperature } from 'pages/reporting/reports/rug/utils/inspectionTemperature'
import { useMemo } from 'react'
import { GeckoEntry, GeckoMeasurementUnit, RolodexEntryType } from 'types'
import { associateBy } from 'utils/fde/maps'

const RUG_TRANSDUCER_TYPE = 'RUG Transducer'
const MAX_SENSOR_SERIALS_PER_PAYLOAD = 24

const parseString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

const normalizeText = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '')

const mapSalesforceUnitTypeToReportedAssetType = (
  salesforceUnitType: string | undefined
): string | undefined => {
  if (!salesforceUnitType) return undefined

  const normalizedType = salesforceUnitType.toLowerCase()
  if (
    normalizedType.includes('storage tank') ||
    normalizedType.includes('tank')
  ) {
    return ReportedAssetType.AST
  }
  if (normalizedType.includes('sphere') || normalizedType.includes('vessel')) {
    return ReportedAssetType.VESSEL
  }
  if (normalizedType.includes('pipe')) {
    return ReportedAssetType.PIPING
  }

  return undefined
}

const getSensorSerialNumbers = (
  entries: readonly GeckoEntry[]
): SensorSerialNumbers => {
  const subComponents = entries.filter(
    (entry) => entry.type === RolodexEntryType.SALESFORCE_RUG_SUBCOMPONENT
  )
  const subComponentAssignments = entries.filter(
    (entry) =>
      entry.type === RolodexEntryType.SALESFORCE_RUG_SUBCOMPONENT_ASSIGNMENT
  )
  const components = entries.filter(
    (entry) => entry.type === RolodexEntryType.SALESFORCE_RUG_COMPONENT
  )

  const componentsById = associateBy({ arr: components, key: 'id' })
  const assignmentBySubComponentId = associateBy({
    arr: subComponentAssignments,
    key: `tags.${RolodexEntryType.SALESFORCE_RUG_SUBCOMPONENT}`,
  })

  const transducersByAsset = subComponents.reduce((agg, subComponent) => {
    const componentId =
      subComponent.tags[RolodexEntryType.SALESFORCE_RUG_COMPONENT]
    const component = componentId ? componentsById.get(componentId) : undefined
    const assetId = component?.tags[RolodexEntryType.SALESFORCE_RUG_ASSET]
    const serialNumber = parseString(subComponent.data.name)
    const subComponentType = parseString(subComponent.data.type)

    if (!component || !assetId || !serialNumber) return agg
    if (subComponentType !== RUG_TRANSDUCER_TYPE) return agg

    const assignment = assignmentBySubComponentId.get(subComponent.id)
    const isSpare = assignment?.data.is_spare === true

    const assetTransducers = agg.get(assetId) ?? []
    assetTransducers.push({ serialNumber, isSpare })
    agg.set(assetId, assetTransducers)

    return agg
  }, new Map<string, { serialNumber: string; isSpare: boolean }[]>())

  const assetTransducerEntries = Array.from(transducersByAsset.values())
  return assetTransducerEntries.reduce((agg, assetTransducers, idx) => {
    const currSerialNumbers = sortBy(
      assetTransducers,
      (transducer) => transducer.isSpare
    )
      .slice(0, MAX_SENSOR_SERIALS_PER_PAYLOAD)
      .map((transducer) => transducer.serialNumber)

    agg[idx] = currSerialNumbers
    return agg
  }, {} as SensorSerialNumbers)
}

const getRobots = (entries: readonly GeckoEntry[]): RobotData[] => {
  const sfAssets = entries.filter(
    (entry) => entry.type === RolodexEntryType.SALESFORCE_RUG_ASSET
  )
  const robotsById = new Map<string, RobotData>()

  sfAssets.forEach((asset) => {
    const serialNumber = parseString(asset.data.name)
    if (!serialNumber) return

    const model = parseString(asset.data.type)
    const key = `${model ?? ''}-${serialNumber}`
    if (!robotsById.has(key)) {
      robotsById.set(key, {
        model,
        serialNumber,
      })
    }
  })

  return Array.from(robotsById.values()).sort((left, right) => {
    const leftValue = `${left.model ?? ''}-${left.serialNumber}`
    const rightValue = `${right.model ?? ''}-${right.serialNumber}`
    return leftValue.localeCompare(rightValue)
  })
}

const getBestMatchingSfUnit = (
  entries: readonly GeckoEntry[],
  unitName: string | undefined
): GeckoEntry | undefined => {
  if (entries.length === 0) return undefined
  if (!unitName) return undefined

  const normalizedUnitName = normalizeText(unitName)

  const exactMatch = entries.find((entry) => {
    const entryName = parseString(entry.data.name)
    return entryName && normalizeText(entryName) === normalizedUnitName
  })
  if (exactMatch) return exactMatch

  const includesMatch = entries.find((entry) => {
    const entryName = parseString(entry.data.name)
    if (!entryName) return false

    const normalizedEntryName = normalizeText(entryName)
    return (
      normalizedEntryName.includes(normalizedUnitName) ||
      normalizedUnitName.includes(normalizedEntryName)
    )
  })
  if (includesMatch) return includesMatch

  return undefined
}

const getSalesforceUnitFallbacks = (
  sfUnitEntries: readonly GeckoEntry[],
  unitName: string | undefined
): Pick<SalesforceReportData, 'assetType' | 'inspectionTemperature'> => {
  const sfUnits = sfUnitEntries.filter(
    (entry) => entry.type === RolodexEntryType.SALESFORCE_UNIT
  )
  const sfUnit = getBestMatchingSfUnit(sfUnits, unitName)
  if (!sfUnit) return {}

  const sfUnitType = parseString(sfUnit.data.type)
  const sfInServiceTemperature = sfUnit.data.in_service_temperature
  return {
    assetType: mapSalesforceUnitTypeToReportedAssetType(sfUnitType),
    inspectionTemperature: normalizeInspectionTemperature(
      typeof sfInServiceTemperature === 'number'
        ? sfInServiceTemperature
        : parseString(sfInServiceTemperature),
      GeckoMeasurementUnit.Celsius
    ),
  }
}

export interface SalesforceReportData {
  sensorSerialNumbers: SensorSerialNumbers
  robots: RobotData[]
  assetType?: string
  inspectionTemperature?: InspectionTemperature
  isLoading: boolean
}

export const useSalesforceReportData = ({
  salesforceJobId,
  salesforceAccountId,
  unitName,
}: {
  salesforceJobId?: string
  salesforceAccountId?: string
  unitName?: string
}): SalesforceReportData => {
  const { entries: equipmentEntries, isLoading: isLoadingEquipment } =
    useRolodexEntries(
      salesforceJobId
        ? [
            {
              type: RolodexEntryType.SALESFORCE_RUG_ASSET_ASSIGNMENT,
              tags: {
                [RolodexEntryType.SALESFORCE_JOB]: [salesforceJobId],
              },
              links: [RolodexEntryType.SALESFORCE_RUG_ASSET],
            },
            {
              type: RolodexEntryType.SALESFORCE_RUG_COMPONENT_ASSIGNMENT,
              tags: {
                [RolodexEntryType.SALESFORCE_JOB]: [salesforceJobId],
              },
              links: [RolodexEntryType.SALESFORCE_RUG_COMPONENT],
            },
            {
              type: RolodexEntryType.SALESFORCE_RUG_SUBCOMPONENT_ASSIGNMENT,
              tags: {
                [RolodexEntryType.SALESFORCE_JOB]: [salesforceJobId],
              },
              links: [RolodexEntryType.SALESFORCE_RUG_SUBCOMPONENT],
            },
          ]
        : undefined
    )

  const { entries: sfUnitEntries, isLoading: isLoadingSfUnits } =
    useRolodexEntries(
      salesforceAccountId
        ? {
            type: RolodexEntryType.SALESFORCE_UNIT,
            tags: {
              [RolodexEntryType.SALESFORCE_ACCOUNT]: salesforceAccountId,
            },
          }
        : undefined
    )

  const sensorSerialNumbers = useMemo(() => {
    return getSensorSerialNumbers(equipmentEntries)
  }, [equipmentEntries])

  const robots = useMemo(() => {
    return getRobots(equipmentEntries)
  }, [equipmentEntries])

  const sfUnitFallbacks = useMemo(() => {
    return getSalesforceUnitFallbacks(sfUnitEntries, unitName)
  }, [sfUnitEntries, unitName])

  return useMemo(
    () => ({
      sensorSerialNumbers,
      robots,
      assetType: sfUnitFallbacks.assetType,
      inspectionTemperature: sfUnitFallbacks.inspectionTemperature,
      isLoading: isLoadingEquipment || isLoadingSfUnits,
    }),
    [
      sensorSerialNumbers,
      robots,
      sfUnitFallbacks.assetType,
      sfUnitFallbacks.inspectionTemperature,
      isLoadingEquipment,
      isLoadingSfUnits,
    ]
  )
}
